import { useEffect, useMemo, useState } from "react";
import { Upload, Search, ChevronRight } from "lucide-react";
import UploadModal from "./CategoriesSection/UploadModal.jsx";
import { useDispatch } from "react-redux";
import { onCategories } from "../../redux/homeAction";
import { useNavigate } from "react-router-dom";
import TopHeader from "./Common/TopHeader.jsx";
import FullScreenLoader from "../Common/FullScreenLoader.jsx";

export default function AddSingleCatalog() {
  const dispatch = useDispatch();

  const [search, setSearch] = useState("");
  const [images, setImages] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedPath, setSelectedPath] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    dispatch(onCategories(setLoading, setCategories));
  }, [dispatch]);

  const handleSelect = (item, level) => {
    const updated = selectedPath.slice(0, level);
    updated[level] = item;
    setSelectedPath(updated);
  };

  const handleUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const previews = files.map((f) => ({
      file: f,
      preview: URL.createObjectURL(f),
    }));

    setImages(previews);
    setShowModal(true);
  };

  const filteredRoot = useMemo(() => {
    return categories.filter((c) =>
      c.name.toLowerCase().includes(search.toLowerCase()),
    );
  }, [categories, search]);

  const columns = useMemo(() => {
    const cols = [filteredRoot];
    selectedPath.forEach((node) => {
      if (node?.children?.length) cols.push(node.children);
    });
    return cols;
  }, [filteredRoot, selectedPath]);

  const last = selectedPath[selectedPath.length - 1];
  const isLeaf = last && (!last.children || last.children.length === 0);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* HEADER */}
      <TopHeader select={1} />

      {/* BODY */}
      <div className="grid grid-cols-12 gap-4 md:gap-5 p-3 md:p-5">
        {/* LEFT */}
        <div className="col-span-12 md:col-span-8">
          <div className="bg-white rounded-xl p-4 md:p-5">
            <h2 className="font-semibold mb-3 md:mb-4 text-sm md:text-base">
              Search Category
            </h2>

            {/* SEARCH */}
            <div className="relative mb-4">
              <Search
                className="absolute left-3 top-3 text-gray-400"
                size={18}
              />

              <input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setSelectedPath([]);
                }}
                className="w-full border rounded-lg py-2 pl-10 text-sm md:text-base focus:ring-2 focus:ring-indigo-500 outline-none"
                placeholder="Search categories..."
              />
            </div>

            {/* CATEGORY COLUMNS */}
            <div className="flex gap-3 md:gap-4 overflow-x-auto pb-2">
              {loading ? (
                <div className="p-3 text-gray-500 text-sm">Loading...</div>
              ) : (
                columns.map((items, level) => (
                  <div
                    key={level}
                    className="min-w-[200px] md:min-w-[260px] bg-white border rounded-lg shadow-sm"
                  >
                    {items.map((item) => {
                      const active = selectedPath[level]?._id === item._id;

                      return (
                        <div
                          key={item._id}
                          onClick={() => handleSelect(item, level)}
                          className={`flex items-center justify-between px-3 md:px-4 py-2 md:py-3 cursor-pointer border-b text-sm transition
                          ${
                            active
                              ? "bg-indigo-600 text-white"
                              : "hover:bg-gray-50"
                          }`}
                        >
                          <span className="truncate">{item.name}</span>

                          {item.children?.length > 0 && (
                            <ChevronRight size={16} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* RIGHT */}
        <div className="col-span-12 md:col-span-4 mt-4 md:mt-0">
          <div className="bg-white rounded-xl p-4 md:p-5">
            {/* BREADCRUMB */}
            <h3 className="font-semibold text-center mb-3 text-sm md:text-base">
              {selectedPath.length
                ? selectedPath.map((i, idx) => (
                    <span key={i._id}>
                      <button
                        onClick={() =>
                          setSelectedPath(selectedPath.slice(0, idx + 1))
                        }
                        className="text-indigo-600 hover:underline text-xs md:text-sm"
                      >
                        {i.name}
                      </button>
                      {idx < selectedPath.length - 1 && " / "}
                    </span>
                  ))
                : "Select Category"}
            </h3>

            <img
              src="/images/guide.png"
              className="mx-auto w-28 md:w-40 mb-4"
            />

            {/* UPLOAD BUTTON */}
            {isLeaf ? (
              <label className="bg-indigo-600 hover:bg-indigo-700 text-white flex items-center justify-center gap-2 py-3 rounded-lg cursor-pointer text-sm md:text-base">
                <Upload size={18} />
                Add Product Images
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/*"
                  onChange={handleUpload}
                />
              </label>
            ) : (
              <div className="bg-gray-200 text-gray-500 py-3 rounded-lg text-center text-sm">
                Select Final Category First
              </div>
            )}

            {/* GUIDELINES */}
            <div className="mt-5 text-sm">
              <h4 className="font-semibold mb-2">Guidelines</h4>

              <ul className="space-y-1 text-gray-600 text-xs md:text-sm">
                <li>• 1–9 products allowed</li>
                <li>• Same category required</li>
                <li>• Front image preferred</li>
                <li>• No watermark/text</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* MOBILE STICKY UPLOAD (optional UX boost) */}
      {isLeaf && (
        <div className="fixed bottom-0 left-0 w-full bg-white border-t p-3 md:hidden">
          <label className="bg-indigo-600 text-white flex items-center justify-center gap-2 py-3 rounded-lg cursor-pointer">
            <Upload size={18} />
            Upload Products
            <input
              type="file"
              hidden
              multiple
              accept="image/*"
              onChange={handleUpload}
            />
          </label>
        </div>
      )}

      {/* MODAL */}
      <UploadModal
        open={showModal}
        images={images}
        setImages={setImages}
        onClose={() => setShowModal(false)}
        selectedPath={selectedPath}
        navigate={navigate}
        setLoading={setLoading}
      />

      {loading && <FullScreenLoader />}
    </div>
  );
}
